import React from "react";
import { ENTRIES } from "../shared/assets/entry_data";
import { PAYOUTS } from "../shared/assets/payouts";
import { useLeaderboard } from "../shared/assets/useLeaderboard";
import { useIsFinal } from "../context/IsFinalContext";

function getPositionNum(golfer, golfers) {
    const g = golfers.find((obj) => obj.name === golfer);
    if (!g) return 99;
    if (g.status === "cut" || g.status === "wd" || g.status === "dsq") return 55;
    return g.position ?? 99;
}

function getPlayerWinnings(golfer, golfers, isFinal) {
    const g = golfers.find((obj) => obj.name === golfer);
    if (!g) return 0;
    if (g.status === "cut") return 25000;
    if (isFinal) return g.winnings ?? 0;
    const pos = g.position;
    if (!pos || pos < 1) return 0;
    return PAYOUTS[pos - 1] ?? 0;
}

function tiebreakComparator(a, b, firstPlaceScore) {
    const aDiff = Math.abs(a.tiebreaker - firstPlaceScore);
    const bDiff = Math.abs(b.tiebreaker - firstPlaceScore);
    return aDiff - bDiff;
}

function Standings() {
    const { isFinal } = useIsFinal();
    const { golfers, lastUpdated, loading, error } = useLeaderboard();
    const [selectedEntry, setSelectedEntry] = React.useState(null);

    if (loading) return <p className="mt-20 text-center">Loading standings...</p>;
    if (error)   return <p className="mt-20 text-center text-red-600">{error}</p>;

    var formatter = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 0,
    });

    // Find first place golfer's total score
    const firstPlaceGolfer = golfers.length > 0 ? golfers[0] : null;
    const firstPlaceScore = firstPlaceGolfer?.toPar ?? 0;

    const entries = ENTRIES.map((entry) => {
        let totPos = 0;
        let totWinnings = 0;
        for (let i = 0; i < entry.golfers.length; i++) {
            totWinnings += getPlayerWinnings(entry.golfers[i], golfers, isFinal);
            totPos += getPositionNum(entry.golfers[i], golfers);
        }
        let avgPos = totPos / 5;
        let dollars = formatter.format(totWinnings);

        return {
            name:        entry.name,
            avgPos:      avgPos,
            totWinnings: totWinnings,
            dollars:     dollars,
            tiebreaker:  entry.tiebreaker,
            golfers:     entry.golfers,
            totPos:      totPos,
        };
    });

    entries.sort((a, b) => {
        const winningsDiff = b.totWinnings - a.totWinnings;
        if (winningsDiff !== 0) return winningsDiff;
        return tiebreakComparator(a, b, firstPlaceScore);
    });

    // Calculate tiebreaker differences for rank comparison
    const entriesWithTiebreakerDiff = entries.map((entry) => ({
        ...entry,
        tiebreakerDiff: Math.abs(entry.tiebreaker - firstPlaceScore)
    }));

    // Assign ranks
    const entriesSorted = entriesWithTiebreakerDiff.map((entry, index) => {
        let rank = index + 1;
        if (index > 0 && 
            entry.totWinnings === entriesWithTiebreakerDiff[index - 1].totWinnings &&
            entry.tiebreakerDiff === entriesWithTiebreakerDiff[index - 1].tiebreakerDiff) {
            rank = entriesWithTiebreakerDiff[index - 1].rank;
        }
        entry.rank = rank;

        return (
            <div className="grid grid-cols-5 m-2 pr-4" key={entry.name}>
                <span className="col-span-1">{entry.rank}</span>
                <span className="place-self-start col-span-2 cursor-pointer hover:text-blue-600" onClick={() => setSelectedEntry(entry)}>{entry.name}</span>
                <span className="col-span-1">{entry.dollars}</span>
                <span className="col-span-1">({entry.tiebreaker})</span>
            </div>
        );
    });

    return (
        <div className="flex flex-col lg:w-4/12 w-100 items-center xbg-pink-200">
            <div className="bg-gray-300 mt-14 rounded-lg pb-4">
                <p className="text-3xl my-6 bg-masters-green masters-label">Standings</p>
                <div className="grid grid-cols-5 m-1 font-bold pr-4">
                    <span className="col-span-1">Rank</span>
                    <span className="place-self-start col-span-2">Name</span>
                    <span className="col-span-1">Winnings</span>
                    <span className="col-span-1">Tiebreak</span>
                </div>
                {entriesSorted}
            </div>
            {lastUpdated && (
                <p className="text-center text-sm text-gray-500 mt-2 mb-4">
                    Last updated: {new Date(lastUpdated).toLocaleTimeString()}
                </p>
            )}
            
            {selectedEntry && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={() => setSelectedEntry(null)}>
                    <div className="bg-white rounded-lg shadow-2xl p-6 w-96 max-h-96 overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                        <div className="flex justify-between items-center mb-4">
                            <h2 className="text-2xl font-bold">{selectedEntry.name}</h2>
                            <button onClick={() => setSelectedEntry(null)} className="text-gray-500 hover:text-gray-700 text-2xl">&times;</button>
                        </div>
                        <div className="text-base">
                            <p className="mb-3"><strong>Total Winnings:</strong> {selectedEntry.dollars}</p>
                            <p className="mb-4"><strong>Average Position:</strong> {(selectedEntry.totPos / 5).toFixed(2)}</p>
                            <p className="font-semibold mb-2">Golfers:</p>
                            <ul className="mb-4 ml-4">
                                {selectedEntry.golfers.map((golfer) => (
                                    <li key={golfer} className="text-sm mb-1">{golfer} - {formatter.format(getPlayerWinnings(golfer, golfers, isFinal))}</li>
                                ))}
                            </ul>
                            <p><strong>Tie-breaker:</strong> {selectedEntry.tiebreaker}</p>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default Standings;